/**
 * Site announcement icon allowlist — single source of truth for API + dashboard picker.
 * Includes every Icon type used in the web app (see scripts/check-announcement-icons.mjs).
 *
 * When adding a new Tabler icon: design-tokens/icons.ts, Icon.tsx, then add here.
 * See .cursor/rules/tabler-icons-announcements.mdc.
 */

export const DEFAULT_ANNOUNCEMENT_ICON = 'bell-ringing';

/** Keys + labels shown in Settings → Announcements (super admin icon grid). */
export const ANNOUNCEMENT_ICON_REGISTRY = [
  // Alerts & messages
  { type: 'bell-ringing', label: 'Alert bell' },
  { type: 'bell', label: 'Bell' },
  { type: 'bell-check', label: 'Notifications read' },
  { type: 'exclamation-triangle', label: 'Warning' },
  { type: 'exclamation-circle', label: 'Important' },
  { type: 'info-circle', label: 'Info' },
  { type: 'message-report', label: 'Message' },
  // Education & school
  { type: 'school', label: 'School' },
  { type: 'book', label: 'Book' },
  { type: 'chalkboard-teacher', label: 'Teaching' },
  { type: 'backpack', label: 'Students' },
  { type: 'clipboard-list', label: 'Registration list' },
  { type: 'smart-home', label: 'Home / dashboard' },
  { type: 'device-ipad-horizontal-search', label: 'Class review' },
  { type: 'star', label: 'Featured / star' },
  { type: 'award', label: 'Award' },
  // People
  { type: 'user', label: 'User' },
  { type: 'user-plus', label: 'Add user' },
  { type: 'user-check', label: 'User verified' },
  { type: 'users', label: 'Families' },
  { type: 'users-group', label: 'Group' },
  { type: 'baby-carriage', label: 'Young children' },
  { type: 'old', label: 'Older students' },
  // Calendar & time
  { type: 'calendar', label: 'Calendar' },
  { type: 'clock', label: 'Clock' },
  { type: 'clock-hour-3', label: 'Schedule time' },
  { type: 'hourglass-low', label: 'Waitlist / pending' },
  { type: 'sunrise', label: 'Morning' },
  { type: 'sun', label: 'Sunny' },
  { type: 'sunglasses', label: 'Sunglasses' },
  { type: 'sunset', label: 'Evening' },
  { type: 'moon', label: 'Night' },
  // Weather
  { type: 'cloud', label: 'Cloudy' },
  { type: 'cloud-rain', label: 'Rain' },
  { type: 'cloud-snow', label: 'Snow' },
  { type: 'snowflake', label: 'Snowflake' },
  { type: 'cloud-storm', label: 'Storm' },
  { type: 'wind', label: 'Wind' },
  { type: 'barrier-block', label: 'Road closed' },
  // Faith & events
  { type: 'building-church', label: 'Church' },
  { type: 'bible', label: 'Bible' },
  { type: 'cross', label: 'Cross' },
  { type: 'confetti', label: 'Celebration' },
  { type: 'balloon', label: 'Party' },
  { type: 'leaf-maple', label: 'Maple leaf' },
  { type: 'christmas-tree', label: 'Christmas tree' },
  { type: 'fish-christianity', label: 'Christian fish' },
  { type: 'pray', label: 'Prayer' },
  { type: 'tools-kitchen-2', label: 'Potluck / meal' },
  // Status
  { type: 'checkmark', label: 'Confirmed' },
  { type: 'circle-check', label: 'Approved' },
  { type: 'progress-check', label: 'Submitted' },
  { type: 'circle-dashed-check', label: 'In progress' },
  { type: 'ban', label: 'Not allowed' },
  // Payments
  { type: 'currency-dollar', label: 'Dollar / fees' },
  { type: 'credit-card', label: 'Credit card' },
  { type: 'brand-paypal', label: 'PayPal' },
  { type: 'coin', label: 'Paid' },
  { type: 'coin-off', label: 'Unpaid / free' },
  { type: 'report-money', label: 'Financial report' },
  // Contact & devices
  { type: 'mail', label: 'Email' },
  { type: 'mail-check', label: 'Email verified' },
  { type: 'phone', label: 'Phone' },
  { type: 'phone-check', label: 'Phone verified' },
  { type: 'qrcode', label: 'QR code' },
  { type: 'device-mobile-message', label: 'Mobile / SMS' },
  { type: 'device-desktop-check', label: 'Desktop' },
  { type: 'device-tablet-check', label: 'Tablet' },
  // Places & files
  { type: 'map-pin', label: 'Location' },
  { type: 'map-search', label: 'Address search' },
  { type: 'file-text', label: 'Document' },
  { type: 'photo', label: 'Image' },
  { type: 'download', label: 'Download' },
  // Actions & UI
  { type: 'search', label: 'Search' },
  { type: 'copy', label: 'Copy' },
  { type: 'adjustments-horizontal', label: 'Settings' },
  { type: 'refresh', label: 'Refresh' },
  { type: 'refresh-alert', label: 'Refresh alert' },
  { type: 'circle-plus', label: 'Add' },
  { type: 'circle-minus', label: 'Minus' },
  { type: 'circle-x', label: 'Close' },
  { type: 'chevron-left', label: 'Back' },
  { type: 'chevron-up', label: 'Up' },
  { type: 'trash', label: 'Delete' },
  { type: 'logout-2', label: 'Log out' },
  { type: 'eye', label: 'Show' },
  { type: 'eye-off', label: 'Hide' },
  { type: 'bold', label: 'Bold' },
  { type: 'italic', label: 'Italic' },
  { type: 'blockquote', label: 'Quote' },
  { type: 'link', label: 'Link' },
  { type: 'thumb-up', label: 'Like' },
  { type: 'mood-smile', label: 'Reaction' },
  { type: 'traffic-cone', label: 'Maintenance' },
  { type: 'shield', label: 'Security' },
  { type: 'shield-check', label: 'Security verified' },
  { type: 'grid-dots', label: 'Grid menu' },
  { type: 'x', label: 'Close' },
] as const;

export const ANNOUNCEMENT_ICON_TYPES = ANNOUNCEMENT_ICON_REGISTRY.map((entry) => entry.type);

export type AnnouncementIconType = (typeof ANNOUNCEMENT_ICON_REGISTRY)[number]['type'];

export function isAnnouncementIconType(value: string): value is AnnouncementIconType {
  return (ANNOUNCEMENT_ICON_TYPES as readonly string[]).includes(value);
}

/** Map legacy or shorthand keys from older clients to the allowlist. */
export function resolveAnnouncementIconKey(value: string | undefined): string | undefined {
  if (value == null) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed === 'barrier-block-filled') return 'barrier-block';
  if (trimmed === 'snow') return 'cloud-snow';
  return trimmed;
}

export function normalizeAnnouncementIconType(value: string | undefined): AnnouncementIconType {
  const resolved = resolveAnnouncementIconKey(value);
  if (resolved && isAnnouncementIconType(resolved)) return resolved;
  return DEFAULT_ANNOUNCEMENT_ICON;
}
